import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { AppErrorBoundary } from "../app/AppErrorBoundary";
import { LandingPage } from "./LandingPage";
import { csrfHeaders } from "./csrf";
import type { AuthState, WorkspaceUser } from "./types";

const loadEditor = async () => {
  const module = await import("../app/App");
  return { default: module.App };
};
const Editor = lazy(loadEditor);

const authPreview = new URLSearchParams(window.location.search).has("authPreview");
const authRequired = import.meta.env.PROD || import.meta.env.VITE_REQUIRE_AUTH === "true" || authPreview;

export function AuthGate() {
  const [state, setState] = useState<AuthState>(() => authRequired ? { authenticated: false, user: null } : {
    authenticated: true,
    user: { email: "local@localhost", name: "Local workspace", picture: null, status: "approved", role: "user" },
  });
  const [checking, setChecking] = useState(authRequired);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshSession = useCallback(async () => {
    if (!authRequired) return;
    try {
      const response = await fetch("/api/auth/session", { credentials: "same-origin" });
      if (!response.ok) { setState({ authenticated: false, user: null }); return; }
      setState(await response.json() as AuthState);
    } catch {
      setState({ authenticated: false, user: null });
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { void refreshSession(); }, [refreshSession]);

  const signIn = async () => {
    setBusy(true);
    setError(null);
    try {
      const configResponse = await fetch("/api/auth/config", { credentials: "same-origin" });
      if (!configResponse.ok) throw new Error("Google sign-in is not configured yet.");
      const { firebaseConfig } = await configResponse.json() as { firebaseConfig: Record<string, string> };
      const [{ initializeApp, getApps }, { GoogleAuthProvider, getAuth, signInWithPopup }] = await Promise.all([import("firebase/app"), import("firebase/auth")]);
      const firebaseApp = getApps()[0] ?? initializeApp(firebaseConfig);
      const credential = await signInWithPopup(getAuth(firebaseApp), new GoogleAuthProvider());
      const idToken = await credential.user.getIdToken();
      const response = await fetch("/api/auth/session", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json", ...csrfHeaders() },
        body: JSON.stringify({ idToken }),
      });
      if (!response.ok) {
        const failure = await response.json().catch(() => ({})) as { reference?: string };
        throw new Error(failure.reference ? `sign-in-failed:${failure.reference}` : "sign-in-failed");
      }
      setState(await response.json() as AuthState);
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : "Google sign-in could not be completed.";
      setError(message.includes("popup-closed")
        ? "The Google sign-in window was closed. Nothing changed."
        : message.includes("popup-blocked") || message.includes("cancelled-popup-request")
          ? "Google sign-in couldn't open. Allow pop-ups and try again."
          : "Google sign-in couldn't be completed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const signOut = async () => {
    setBusy(true);
    try { await fetch("/api/auth/session", { method: "DELETE", credentials: "same-origin", headers: csrfHeaders() }); } finally {
      setState({ authenticated: false, user: null });
      setBusy(false);
    }
  };

  if (checking) return <main className="auth-loading" aria-live="polite">Opening your workspace…</main>;
  const user = state.user;
  if (!state.authenticated || !user || user.status !== "approved") {
    return <LandingPage status={user?.status ?? "signed-out"} busy={busy} error={error} onSignIn={() => void signIn()} onSignOut={() => void signOut()} />;
  }
  return <AppErrorBoundary><Suspense fallback={<main className="auth-loading">Loading the canvas…</main>}><Editor currentUser={authRequired ? user : null} onSignOut={() => void signOut()} /></Suspense></AppErrorBoundary>;
}
